import { useParams, Link, Navigate } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import SiteHeader from "@/components/SiteHeader";
import VideoBanner, { SidebarVideoBanners } from "@/components/blackbox/VideoBanner";
import { articles } from "@/data/articles";
import { ChevronLeft, BookOpen, FileText, ExternalLink, Calendar } from "lucide-react";
import { 
  getPostSlug, 
  findPostBySlug, 
  shouldRedirectToSeoSlug,
  generateSlugFromTitle 
} from "@/utils/slugUtils";

export const generateSlug = generateSlugFromTitle;

const getRelatedArticles = (currentPost: typeof articles[0], count: number = 3) => {
  return articles
    .filter(p => p.id !== currentPost.id && p.category === currentPost.category)
    .slice(0, count);
};

const Article = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? findPostBySlug(slug) : undefined;
  
  if (article && slug) {
    const redirectSlug = shouldRedirectToSeoSlug(slug, article);
    if (redirectSlug) {
      return <Navigate to={`/artigos/${redirectSlug}`} replace />;
    }
  }
  
  if (!article) {
    return (
      <div className="min-h-screen bg-[#f6f7f9]">
        <SiteHeader />
        <main className="max-w-2xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Artigo não encontrado</h1>
          <Link to="/artigos" className="text-[#4267B2] hover:underline">← Voltar para Artigos</Link>
        </main>
      </div>
    );
  }

  const canonicalSlug = getPostSlug(article);
  const relatedArticles = getRelatedArticles(article);

  // Medical condition mapping for rich schema
  const getMedicalConditionSchema = (category: string) => {
    const conditions: Record<string, object> = {
      "Pearly Papules": {
        "@type": "MedicalCondition",
        "name": "Pearly Penile Papules",
        "alternateName": ["PPP", "Hirsuties coronae glandis"],
        "description": "Small dome-shaped bumps around the penis corona. Normal anatomical variation in 14-48% of men.",
        "associatedAnatomy": { "@type": "AnatomicalStructure", "name": "Penis corona" },
        "possibleTreatment": { "@type": "MedicalTherapy", "name": "Electrocauterization" }
      },
      "Tyson's Glands": {
        "@type": "MedicalCondition",
        "name": "Tyson Glands",
        "alternateName": "Tyson's glands",
        "description": "Sebaceous glands on the frenulum producing natural lubricant. Normal anatomy.",
        "associatedAnatomy": { "@type": "AnatomicalStructure", "name": "Penis frenulum" }
      },
      "Glândulas de Tyson": {
        "@type": "MedicalCondition",
        "name": "Glândulas de Tyson",
        "alternateName": ["Tyson glands", "Glândulas sebáceas modificadas do prepúcio"],
        "description": "Glândulas sebáceas modificadas localizadas no frênulo e prepúcio. Variação anatômica normal, não-patológica, não-contagiosa.",
        "associatedAnatomy": { "@type": "AnatomicalStructure", "name": "Frênulo do p-nis" }
      },
      "Penile Pimples": {
        "@type": "MedicalCondition",
        "name": "Penile Bumps",
        "description": "Various types of bumps on the penis including normal anatomical variations and conditions requiring attention.",
        "differentialDiagnosis": ["Pearly Penile Papules", "Fordyce Spots", "Genital Warts"]
      }
    };
    return conditions[category] || { "@type": "MedicalCondition", "name": category };
  };

  // MedicalWebPage Schema for E-E-A-T
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "headline": article.title,
    "description": article.metaDescription,
    "author": {
      "@type": "Organization",
      "name": "Equipe de Saúde Método P-nis Liso",
      "url": "https://smothmethod.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Método P-nis Liso",
      "logo": { "@type": "ImageObject", "url": "https://smothmethod.com/logo.jpg" }
    },
    "datePublished": "2024-01-15",
    "dateModified": article.lastUpdated,
    "lastReviewed": article.lastUpdated,
    "reviewedBy": {
      "@type": "Organization",
      "name": "Equipe de Saúde Método P-nis Liso"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://smothmethod.com/artigos/${canonicalSlug}`
    },
    "about": getMedicalConditionSchema(article.category),
    "keywords": article.keywords.join(", "),
    "citation": article.references.map(ref => ({
      "@type": "CreativeWork",
      "name": ref.title,
      "url": ref.url
    })),
    "medicalAudience": {
      "@type": "MedicalAudience",
      "audienceType": "Patient"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://smothmethod.com/" },
      { "@type": "ListItem", "position": 2, "name": "Artigos", "item": "https://smothmethod.com/artigos" },
      { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://smothmethod.com/artigos/${canonicalSlug}` }
    ]
  };

  const faqSchema = article.faq && article.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      <SidebarVideoBanners />
      <Helmet>
        <title>{article.metaTitle}</title>
        <meta name="description" content={article.metaDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://smothmethod.com/artigos/${canonicalSlug}`} />
        <link rel="alternate" hrefLang="pt-br" href={`https://smothmethod.com/artigos/${canonicalSlug}`} />
        <link rel="alternate" hrefLang="x-default" href={`https://smothmethod.com/artigos/${canonicalSlug}`} />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.metaTitle} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:url" content={`https://smothmethod.com/artigos/${canonicalSlug}`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={article.metaTitle} />
        <meta name="twitter:description" content={article.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <SiteHeader />
      
      <main className="max-w-2xl lg:max-w-3xl mx-auto px-4 py-6">
        <nav className="mb-4 text-sm" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-gray-500">
            <li><Link to="/artigos" className="hover:text-[#4267B2] flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Artigos</Link></li>
            <li>/</li>
            <li className="text-gray-700">{article.category}</li>
          </ol>
        </nav>

        <article className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <header className="p-6 border-b border-gray-100">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-medium text-[#4267B2] bg-[#4267B2]/10 px-2 py-0.5 rounded">{article.category}</span>
              <span className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" />Atualizado: {article.lastUpdated}</span>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight mb-3">{article.title}</h1>
            {article.medicalReviewer && (
              <p className="text-xs text-gray-500 italic">{article.medicalReviewer}</p>
            )}
          </header>

          <section className="p-6">
            <div className="prose prose-sm md:prose-base max-w-none text-gray-700 [&_.lead]:text-lg [&_.lead]:text-gray-800 [&_.lead]:mb-6 [&_.highlight-box]:bg-blue-50 [&_.highlight-box]:p-4 [&_.highlight-box]:rounded-lg [&_.highlight-box]:border [&_.highlight-box]:border-blue-100 [&_.highlight-box]:my-4"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
            />

            {article.faq && article.faq.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Perguntas frequentes</h2>
                <dl className="space-y-4">
                  {article.faq.map((item, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 bg-white">
                      <dt className="font-semibold text-gray-900 mb-2">{item.question}</dt>
                      <dd className="text-sm text-gray-700 leading-relaxed">{item.answer}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            
            {/* Medical References */}
            {article.references.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Referências Médicas</h3>
                <ul className="space-y-2">
                  {article.references.map((ref, index) => (
                    <li key={index} className="text-xs text-gray-600 flex items-start gap-2">
                      <ExternalLink className="w-3 h-3 mt-0.5 shrink-0 text-[#4267B2]" />
                      <a href={ref.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#4267B2] hover:underline">
                        {ref.source}: {ref.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Quer saber mais?</strong> Leia nosso guia completo.
                <Link to="/guias/pequenas-bolinhas-no-penis" className="text-[#4267B2] hover:underline ml-1">Ler o guia completo →</Link>
              </p>
            </div>

            {/* Video Banner after article content */}
            <VideoBanner variant="footer" />
          </section>
        </article>

        {relatedArticles.length > 0 && (
          <section className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Artigos Relacionados</h2>
            <div className="space-y-3">
              {relatedArticles.map(relatedArticle => (
                <Link key={relatedArticle.id} to={`/artigos/${getPostSlug(relatedArticle)}`} className="block bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                  <span className="text-xs font-medium text-[#4267B2] bg-[#4267B2]/10 px-2 py-0.5 rounded">{relatedArticle.category}</span>
                  <h3 className="font-medium text-gray-900 mt-2 hover:text-[#4267B2]">{relatedArticle.title}</h3>
                </Link>
              ))}
            </div>
          </section>
        )}

        <aside className="mt-8 bg-[#4267B2]/5 border border-[#4267B2]/20 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#4267B2]" />Guia Completo</h3>
          <p className="text-sm text-gray-600 mb-3">Tenha informações completas sobre todos os tipos de bolinhas no p-nis.</p>
          <Link to="/guias/pequenas-bolinhas-no-penis" className="inline-flex items-center text-[#4267B2] font-medium text-sm hover:underline">Ler: Guia Completo →</Link>
        </aside>

        {/* Footer Video Banner */}
        <VideoBanner variant="footer" />

        <footer className="text-center py-6 mt-8 border-t border-gray-200">
          <p className="text-xs text-gray-500">Este conteúdo é apenas para fins educativos. Sempre consulte um profissional médico em caso de dúvidas de saúde.</p>
        </footer>
      </main>
    </div>
  );
};

export default Article;
